import { Navigate, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Box, Typography, Button, Alert, CircularProgress } from "@mui/material";
import { TrendingUp, Zap, Mail } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useGoogleLogin } from "@react-oauth/google";
import {
  loginWithGoogle, clearError,
  selectIsAuthenticated, selectAuthLoading, selectAuthError,
} from "../store/slices/authSlice";
import HyrvixLogo from "../components/HyrvixLogo";

const FEATURES = [
  { icon: TrendingUp, label: "ATS score & keyword gap analysis" },
  { icon: Zap,        label: "AI-powered resume optimization" },
  { icon: Mail,       label: "Instant cover letter generation" },
];

// Google "G" logo SVG
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" style={{ display: "block" }}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

export default function Login() {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const authLoading = useSelector(selectAuthLoading);
  const authError = useSelector(selectAuthError);

  const [authCheckTimedOut, setAuthCheckTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAuthCheckTimedOut(true), 2000);
    return () => clearTimeout(t);
  }, []);

  // Must be called before any conditional returns
  const triggerGoogleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      dispatch(loginWithGoogle(tokenResponse.access_token));
    },
    onError: () => {
      dispatch(clearError());
    },
  });

  if (authLoading && !authCheckTimedOut) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f8fafc">
        <CircularProgress size={32} sx={{ color: "#2563eb" }} />
      </Box>
    );
  }

  if (isAuthenticated) return <Navigate to="/" replace />;

  const handleGoogleLogin = () => {
    dispatch(clearError());
    triggerGoogleLogin();
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* ── Left brand panel ── */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          width: "48%",
          flexShrink: 0,
          background: "linear-gradient(145deg, #1e3a8a 0%, #1e40af 40%, #6d28d9 100%)",
          px: { md: 6, lg: 10 },
          py: 8,
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {[
          { size: 320, top: -100, right: -80, opacity: 0.07 },
          { size: 200, bottom: -60, left: -60, opacity: 0.07 },
          { size: 120, top: "45%", right: 40, opacity: 0.05 },
        ].map((c, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute", width: c.size, height: c.size,
              borderRadius: "50%", background: "#fff", opacity: c.opacity,
              top: c.top, bottom: c.bottom, left: c.left, right: c.right,
            }}
          />
        ))}

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 5 }}>
            <HyrvixLogo size={42} />
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "-0.3px" }}>Hyrvix</Typography>
          </Box>

          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 2, letterSpacing: "-0.5px" }}>
            Land your dream job faster
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.75, mb: 5, lineHeight: 1.6, maxWidth: 340 }}>
            AI-powered resume analysis that shows exactly what recruiters and ATS systems want to see.
          </Typography>

          {FEATURES.map(({ icon: Icon, label }) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: "9px", background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon size={18} />
              </Box>
              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>{label}</Typography>
            </Box>
          ))}

          <Box sx={{ mt: 6, p: 3, borderRadius: "12px", background: "rgba(255,255,255,0.1)", backdropFilter: "blur(10px)", border: "1px solid rgba(255,255,255,0.15)" }}>
            <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6, fontStyle: "italic" }}>
              "My ATS score went from 38% to 91%. I had 4 interview calls the following week."
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.65, mt: 1.5, display: "block", fontWeight: 600 }}>
              — Rohan M., Senior SDE at a top startup
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Right auth panel ── */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          bgcolor: "#f8fafc",
          // On mobile: push content toward the bottom half so it doesn't look empty at top
          justifyContent: { xs: "flex-start", md: "center" },
          pt: { xs: 0, md: 0 },
        }}
      >
        {/* Mobile top nav */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            px: 3,
            pt: 3,
            pb: 2,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HyrvixLogo size={32} />
            <Typography sx={{ fontWeight: 800, color: "#1e40af", fontSize: 17 }}>Hyrvix</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Typography
              component={Link}
              to="/"
              sx={{ fontSize: 13, fontWeight: 600, color: "#2563eb", textDecoration: "none", "&:hover": { color: "#1d4ed8" } }}
            >
              ← Home
            </Typography>
            <Typography
              component={Link}
              to="/about"
              sx={{ fontSize: 13, fontWeight: 600, color: "#64748b", textDecoration: "none", "&:hover": { color: "#2563eb" } }}
            >
              About
            </Typography>
            <Typography
              component={Link}
              to="/pricing"
              sx={{ fontSize: 13, fontWeight: 600, color: "#64748b", textDecoration: "none", "&:hover": { color: "#2563eb" } }}
            >
              Pricing
            </Typography>
          </Box>
        </Box>

        {/* Mobile hero banner — rich version */}
        <Box
          sx={{
            display: { md: "none" },
            mx: 3,
            mb: 3,
            borderRadius: "20px",
            background: "linear-gradient(145deg, #1e3a8a 0%, #1e40af 45%, #6d28d9 100%)",
            p: 3,
            color: "#fff",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative circles */}
          {[
            { size: 160, top: -50, right: -40, opacity: 0.07 },
            { size: 80, bottom: -20, left: -20, opacity: 0.07 },
          ].map((c, i) => (
            <Box key={i} sx={{ position: "absolute", width: c.size, height: c.size, borderRadius: "50%", background: "#fff", opacity: c.opacity, top: c.top, bottom: c.bottom, left: c.left, right: c.right }} />
          ))}

          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.3rem", lineHeight: 1.25, mb: 0.5 }}>
              Land your dream job faster
            </Typography>
            <Typography sx={{ fontSize: 13, opacity: 0.75, lineHeight: 1.5, mb: 2.5 }}>
              AI that shows exactly what recruiters and ATS systems want to see.
            </Typography>

            {/* Feature list */}
            {FEATURES.map(({ icon: Icon, label }) => (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                <Box sx={{ width: 30, height: 30, borderRadius: "8px", background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon size={15} />
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 500, opacity: 0.9 }}>{label}</Typography>
              </Box>
            ))}

            {/* Testimonial */}
            <Box sx={{ mt: 2.5, p: 2, borderRadius: "10px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)" }}>
              <Typography sx={{ fontSize: 12.5, opacity: 0.9, lineHeight: 1.6, fontStyle: "italic" }}>
                "My ATS score went from 38% to 91%. I had 4 interview calls the next week."
              </Typography>
              <Typography sx={{ fontSize: 11, opacity: 0.6, mt: 1, fontWeight: 600 }}>
                — Rohan M., Senior SDE at a top startup
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Auth card */}
        <Box
          sx={{
            mx: { xs: 3, sm: "auto" },
            width: { sm: "100%", md: "100%" },
            maxWidth: { xs: "100%", sm: 420, md: 420 },
            px: { xs: 0, sm: 0, md: 6 },
          }}
        >
          {/* Desktop welcome text */}
          <Box sx={{ display: { xs: "none", md: "block" }, mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.75 }}>
              Welcome back
            </Typography>
            <Typography variant="body1" sx={{ color: "#64748b", lineHeight: 1.5 }}>
              Sign in to optimize your resume with AI
            </Typography>
          </Box>

          {/* Mobile sign-in label */}
          <Typography
            variant="body2"
            sx={{ display: { md: "none" }, color: "#64748b", mb: 2, lineHeight: 1.5, fontWeight: 500 }}
          >
            Sign in to get started
          </Typography>

          {authError && authError !== "CANCELLED" && authError !== "REDIRECT" && (
            <Alert
              severity="error"
              onClose={() => dispatch(clearError())}
              sx={{ mb: 3, borderRadius: "10px", fontSize: 13 }}
            >
              {authError}
            </Alert>
          )}

          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={handleGoogleLogin}
            disabled={authLoading}
            startIcon={authLoading ? <CircularProgress size={18} /> : <GoogleIcon />}
            sx={{
              py: 1.6,
              borderRadius: "10px",
              fontWeight: 600,
              fontSize: 15,
              borderColor: "#e2e8f0",
              color: "#0f172a",
              bgcolor: "#fff",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
              "&:disabled": { opacity: 0.6 },
            }}
          >
            {authLoading ? "Signing in…" : "Continue with Google"}
          </Button>

          {/* Footer links */}
          <Box sx={{ mt: 4, display: "flex", justifyContent: "center", gap: 3 }}>
            <Typography
              component={Link}
              to="/"
              sx={{ fontSize: 12, color: "#2563eb", textDecoration: "none", fontWeight: 600, "&:hover": { color: "#1d4ed8" } }}
            >
              ← Home
            </Typography>
            <Typography
              component={Link}
              to="/about"
              sx={{ fontSize: 12, color: "#94a3b8", textDecoration: "none", fontWeight: 500, "&:hover": { color: "#2563eb" } }}
            >
              About
            </Typography>
            <Typography
              component={Link}
              to="/pricing"
              sx={{ fontSize: 12, color: "#94a3b8", textDecoration: "none", fontWeight: 500, "&:hover": { color: "#2563eb" } }}
            >
              Pricing
            </Typography>
          </Box>

          <Typography variant="caption" sx={{ display: "block", textAlign: "center", color: "#cbd5e1", mt: 2 }}>
            By continuing you agree to our Terms &amp; Privacy Policy
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
